import Button from "components/form/Button";
import { SettingsValue } from "types/payments";
import EditIcon from "icons/subscribe/EditIcon";
import moment from "moment";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { addNotification, useUserData } from "store/slices/authSlice";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import {
  duplicatePaymentGet,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
} from "store/slices/paymentsSlice";
import {
  chargeCustomerOnSubscribe,
  updatePlan,
} from "store/slices/subscriptionSlice";
import { formatPaymentListDate } from "utils/DateFormat";
import {
  decode,
  formatCurrency,
  frequencyName,
  getPaymentMethod,
} from "utils/HelperFunctions";
import { RootState } from "store/store";

interface UpdateSubscribePreviewProps {
  setCurrentStep: any;
  newProducts: any;
  currentPlan: any;
  grandTotal: number;
  setPaymentProcess: any;
  setScreen: any;
  amountDue?: number;
  proratedCharge?: number;
  proratedCredit?: number;
  startDate?: any;
  id?: any;
  errrorMessage?: any;
  setErrorMessage?: any;
}

const UpdateSubscribePreview = forwardRef(
  (props: UpdateSubscribePreviewProps, ref) => {
    const {
      setCurrentStep,
      newProducts,
      currentPlan,
      setPaymentProcess,
      setScreen,
      amountDue = 0,
      proratedCharge = 0,
      proratedCredit = 0,
      startDate = moment(),
      id,
      errrorMessage,
      setErrorMessage,
    } = props;
    const userData = useUserData();
    const { companyDetails, checkedDuplicatePayment } = useAppSelector(
      (state: RootState) => state.payment
    );
    const showFinalDisclosure = useShowFinalDisclosure();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const portalSettings = usePortalSettingsData();
    const [showDetails, setShowDetails] = useState(false);
    const onUpdatePlan = async (order_id: any) => {
      setIsLoading(true);
      try {
        let plan: any[] = [];
        newProducts.forEach((product: any) => {
          plan.push({
            qty: product?.quantity,
            product_id: product.id,
            price_id: product.pid,
          });
        });
        let payload = {
          subscription_slug: id,
          plan,
          email: currentPlan?.email || "",
          x_payment_method: currentPlan?.payment_method,
          billing_city: currentPlan?.billing_city || "",
          account_number: currentPlan?.account_number || "",
          routing_number: currentPlan?.routing_number || "",
          bank_address: currentPlan?.bank_address || "",
          bank_name: currentPlan?.bank_name || "",
          gateway_id: currentPlan?.gateway_id || "",
          cc_4digit: currentPlan?.cc_4digit || "",
          cc_type: currentPlan?.cc_type || "",
          cc_name: currentPlan?.cc_name || "",
          cc_cvv: currentPlan?.cc_cvv || "",
          cc_expiry: currentPlan?.cc_expiry || "",
          cc_token: currentPlan?.cc_ref_number || "",
          billing_state: currentPlan?.billing_state || "",
          billing_zip: currentPlan?.billing_zip || "",
          billing_address2: currentPlan?.billing_address2 || "",
          billing_address1: currentPlan?.billing_address1 || "",
          amount: amountDue,
          cppid: currentPlan?.cpp_id || "",
          order_id,
          accounts_id: currentPlan?.accounts_id,
          accountid: userData?.data?.accountData?.accountid,
          lead_id: currentPlan?.lead_id,
          lead_owner: currentPlan?.lead_owner,
        };
        let res: any = await dispatch(updatePlan(payload));
        if (res?.payload?.body?.success) {
          setPaymentProcess(true);
          setScreen(true);
          let subscription = decode(id || "")?.split("_");
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "subscription",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "modified",
              activity_type: "subscription_event",
              clusterid: subscription[1],
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: amountDue,
              subscription_id: subscription[0],
              comments: `Subscription for ${userData?.data?.accountData?.accountid} was updated`,
            })
          );
        } else {
          setErrorMessage(res?.payload?.body?.data?.msg);
          setPaymentProcess(true);
          setScreen(false);
        }
      } catch (error) {
        console.log("onUpdatePlan error =====", error);
      } finally {
        setCurrentStep((state: number) => state + 1);
        setIsLoading(false);
      }
    };

    const createPayload = async () => {
      let payload: any = { slug: id };
      switch (currentPlan?.payment_method) {
        case "CC":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "CC",
            amount: amountDue,
            subscription_slug: id,
            payment_method_types: "card",
          };
          break;
        case "ACH":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "ACH",
            payment_method_types: "us_bank_account",
            amount: amountDue,
            subscription_slug: id,
          };
          break;
        case "CAPP":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "CAPP",
            payment_method_types: "cashapp",
            amount: amountDue,
            subscription_slug: id,
          };
          break;
        case "AP":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "AP",
            payment_method_types: "cashapp",
            amount: amountDue,
            subscription_slug: id,
          };
          break;
        case "GP":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "GP",
            amount: amountDue,
            subscription_slug: id,
          };
          break;
        case "AMZP":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "AMZP",
            payment_method_types: "amazon_pay",
            amount: amountDue,
            subscription_slug: id,
          };
          break;
        default:
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: currentPlan?.payment_method,
            amount: amountDue,
            subscription_slug: id,
          };
      }
      return payload;
    };

    const onChargeCustomer = async () => {
      setIsLoading(true);
      if (amountDue > 0) {
        try {
          let payload: any = await createPayload();
          payload = {
            ...payload,
            accounts_id: currentPlan?.accounts_id,
            lead_id: currentPlan?.lead_id,
            email: currentPlan?.email,
            accountid: userData?.data?.accountData?.accountid,
            clusterid: decode(id)?.split("_")?.[1],
            first_name: userData?.data?.accountData?.first_name,
            last_name: userData?.data?.accountData?.last_name,
            address1: currentPlan?.billing_address1,
            city: currentPlan?.billing_city,
            state: currentPlan?.billing_state,
            postal_code: currentPlan?.billing_zip,
            phone_number: currentPlan?.phone_number,
          };

          let res: any = await dispatch(
            chargeCustomerOnSubscribe(payload)
          ).unwrap();
          if (res?.body?.success) {
            await onUpdatePlan(res?.body?.data?.invoice_id);
          } else {
            setCurrentStep((state: number) => state + 1);
            setPaymentProcess(true);
            setScreen(false);
            setIsLoading(false);
          }
        } catch (error) {
          setCurrentStep((state: number) => state + 1);
          setPaymentProcess(true);
          setScreen(false);
          setIsLoading(false);
          console.log("error =====", error);
        } finally {
          dispatch(onSetShowDisclosure(false));
          dispatch(onSetFinalDisclosureLoading(false));
        }
      } else {
        setIsLoading(true);
        try {
          let plan: any[] = [];
          newProducts.forEach((product: any) => {
            plan.push({
              qty: product?.quantity,
              product_id: product.id,
              price_id: product.pid,
            });
          });
          let payload = {
            subscription_slug: id,
            plan,
            email: currentPlan?.email || "",
            x_payment_method: currentPlan?.payment_method,
            billing_city: currentPlan?.billing_city || "",
            account_number: currentPlan?.account_number || "",
            routing_number: currentPlan?.routing_number || "",
            bank_address: currentPlan?.bank_address || "",
            bank_name: currentPlan?.bank_name || "",
            gateway_id: currentPlan?.gateway_id || "",
            cc_4digit: currentPlan?.cc_4digit || "",
            cc_type: currentPlan?.cc_type || "",
            cc_name: currentPlan?.cc_name || "",
            cc_cvv: currentPlan?.cc_cvv || "",
            cc_expiry: currentPlan?.cc_expiry || "",
            cc_token: currentPlan?.cc_ref_number || "",
            billing_state: currentPlan?.billing_state || "",
            billing_zip: currentPlan?.billing_zip || "",
            billing_address2: currentPlan?.billing_address2 || "",
            billing_address1: currentPlan?.billing_address1 || "",
            amount: amountDue,
            cppid: currentPlan?.cpp_id || "",
            // order_id,
            accounts_id: currentPlan?.accounts_id,
            accountid: userData?.data?.accountData?.accountid,
            lead_id: currentPlan?.lead_id,
            lead_owner: currentPlan?.lead_owner,
          };
          let res: any = await dispatch(updatePlan(payload));
          if (res?.payload?.body?.success) {
            setPaymentProcess(true);
            setScreen(true);
            let subscription = decode(id || "")?.split("_");
            await dispatch(
              addNotification({
                initiated_by: "contact",
                event_type: "subscription",
                userid: userData?.data?.accountData?.leadData?.user_id,
                email: "",
                event_status: "modified",
                activity_type: "subscription_event",
                clusterid: subscription[1],
                communication_status: "",
                accounts_id: userData?.data?.accountData?.id,
                payment_amount: amountDue,
                subscription_id: subscription[0],
                comments: `Subscription for ${userData?.data?.accountData?.accountid} was updated`,
              })
            );
          } else {
            setErrorMessage(res?.payload?.body?.data?.msg);
            setPaymentProcess(true);
            setScreen(false);
          }
        } catch (error) {
          console.log("onUpdatePlan error =====", error);
        } finally {
          setCurrentStep((state: number) => state + 1);
          setIsLoading(false);
          dispatch(onSetShowDisclosure(false));
          dispatch(onSetFinalDisclosureLoading(false));
        }
      }
    };

    const checkPastAmount = async (method?: string) => {
      try {
        let payload = {
          accounts_id: userData?.data?.accountData?.id,
          payment_method: currentPlan?.payment_method,
          amount: amountDue,
        };
        let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
        const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
        if (isDuplicateOrders && duplicateOrdersCount > 0) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleCheckDisclosureAndDuplicate = async ({
      method,
    }: {
      method?: string;
    }) => {
      try {
        let moveAhead = !checkedDuplicatePayment
          ? await checkPastAmount(method)
          : true;
        if (!moveAhead) {
          dispatch(onSetFinalDisclosureLoading(false));
          return false;
        } else {
          if (
            (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
              portalSettings?.payment_agreement_opt ===
                SettingsValue.DEFAULT) &&
            !showFinalDisclosure
          ) {
            dispatch(onSetFinalDisclosureLoading(false));
            dispatch(resetDuplicateOrder());
            dispatch(onSetShowDisclosure(true));

            return false;
          } else {
            dispatch(resetDuplicateOrder());
            dispatch(resetCheckDuplicatePayment());
            return true;
          }
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleSubmit = async () => {
      setIsLoading(true);
      try {
        let moveAhead = await handleCheckDisclosureAndDuplicate({});
        if (!moveAhead) {
          setIsLoading(false);
          return;
        }
        onChargeCustomer();
      } catch (error) {
        console.log("error =====", error);
      }
    };

    useImperativeHandle(ref, () => ({
      callHandleClick: () => handleSubmit(),
    }));

    let creditAmount =
      proratedCredit < 0
        ? `(${formatCurrency(proratedCredit || 0)})`
        : formatCurrency(proratedCredit || 0);
    let chargeAmount =
      proratedCharge < 0
        ? `(${formatCurrency(proratedCharge || 0)})`
        : formatCurrency(proratedCharge || 0);

    const shouldShowPaymentMethod =
      currentPlan?.payment_collection_type === null ||
      currentPlan?.payment_collection_type === "MOF";

    const isDowngrade = amountDue <= 0;
    const isUpgrade = amountDue > 0;
    const displayAmountDue = isDowngrade ? 0 : amountDue;

    const renderDetailsPanel = () => {
      if (!showDetails) return null;

      if (isUpgrade) {
        return (
          <div className="lg:p-4 p-3 bg-bgSecondary w-full rounded-md flex flex-col gap-4 animate__animated animate__fadeIn">
            <div className="w-full flex justify-between">
              <span className="text-txtPrimary text-sm leading-[18.23px]">
                Prorated credit{" "}
              </span>
              <span className="text-txtPrimary text-sm leading-[18.23px]">
                {creditAmount}
              </span>
            </div>
            <div className="w-full flex justify-between">
              <span className="text-txtPrimary text-sm leading-[18.23px]">
                Prorated charge{" "}
              </span>
              <span className="text-txtPrimary text-sm leading-[18.23px]">
                {chargeAmount}
              </span>
            </div>
            <div className="w-full flex justify-between font-bold">
              <span className="text-txtPrimary text-sm leading-[18.23px] ">
                Total
              </span>
              <span className="text-txtPrimary text-sm leading-[18.23px]">
                {formatCurrency(amountDue)}
              </span>
            </div>
          </div>
        );
      }

      return null;
    };

    const handlePaymentMethodClick = () => {
      if (!isLoading) {
        setCurrentStep((state: number) => state + 1);
      }

      // if (amountDue === 0) {
      //   if (amountDue > 0) {
      //     setCurrentStep((state: number) => state + 1);
      //   }
      // } else if (isDowngrade) {
      //   if (!isLoading) {
      //     setCurrentStep((state: number) => state + 1);
      //   }
      // } else {
      //   setCurrentStep((state: number) => state + 1);
      // }
    };

    return (
      <div className="w-full py-5 flex flex-col lg:gap-[30px] gap-6 lg:px-6 px-5 transition-all ease-in-out duration-500">
        {newProducts &&
          newProducts?.length &&
          newProducts?.map((item: any, index: number) => {
            return (
              <div
                className="w-full flex justify-between items-center lg:px-4"
                key={index}
              >
                <div className="flex flex-col gap-4">
                  <div className="w-full flex gap-2 items-center text-txtParagraph lg:text-[22px] text-xl capitalize font-medium lg:leading-[28.64px] leading-[26.04px]">
                    {item?.name?.toLowerCase()}
                    <span className="lg:text-base text-sm text-txtSecondary">
                      {item?.quantity > 1 ? ` (x${item?.quantity})` : ""}
                    </span>
                  </div>
                  <div className="w-full lg:text-base text-sm text-txtPrimary">
                    <div className="flex w-full justify-between items-center ">
                      <span>
                        What you’ll pay{" "}
                        {frequencyName(
                          item?.frequency,
                          item?.interval_custom_count,
                          item?.interval_custom_selector
                        )}{" "}
                        starting
                      </span>
                      <span className="font-medium"></span>
                    </div>
                    <div className="pt-1 w-full">
                      {formatPaymentListDate(
                        moment(startDate).format("MM/DD/YYYY"),
                        userData?.data?.accountData?.timezone,
                        "long"
                      )}
                    </div>
                  </div>
                </div>
                <p className="text-base font-medium leading-[20.83px]">
                  {formatCurrency(item?.amount * (item?.quantity || 1)) ||
                    "$0.00"}
                </p>
              </div>
            );
          })}
        <span className="lg:px-4">
          <div className="w-full h-[1px] bg-strokePrimary " />
        </span>

        {renderDetailsPanel()}

        <div className="w-full lg:text-base text-sm text-txtPrimary lg:px-4 animate__animated animate__fadeIn">
          <div className="flex w-full justify-between items-center ">
            <span>Amount due today</span>
            <span className="font-medium">
              {formatCurrency(displayAmountDue) || "$0.00"}
            </span>
          </div>
          {shouldShowPaymentMethod && (
            <div className="pt-2 w-full flex justify-between items-center">
              {isUpgrade && (
                <span
                  className="text-sm text-txtSecondary leading-[18.23px] cursor-pointer underline"
                  onClick={() => {
                    if (isUpgrade) setShowDetails(!showDetails);
                  }}
                >
                  {showDetails ? "Hide details" : "View details"}
                </span>
              )}

              {isDowngrade && (
                <span
                  className="text-sm text-txtSecondary leading-[18.23px] cursor-pointer underline"
                  onClick={() => {}}
                >
                  {" "}
                </span>
              )}
              <span
                className="flex gap-2 justify-end items-center cursor-pointer"
                onClick={handlePaymentMethodClick}
              >
                {getPaymentMethod({
                  card_type: currentPlan?.cc_type,
                  method: currentPlan?.payment_method,
                  digits: currentPlan?.cc_4digit,
                  bank_name: currentPlan?.bank_name || "",
                  account_number:
                    currentPlan?.account_number || currentPlan?.cc_4digit,
                  className: "",
                  textClassName: "!text-sm !text-txtSecondary",
                  isHidden: true,
                  rowId: id
                })}
                {(isUpgrade || isDowngrade || amountDue > 0) && (
                  <EditIcon
                    className="group "
                    _className="group-hover:!fill-iconObscure"
                  />
                )}
              </span>
            </div>
          )}
        </div>

        <div className="lg:pt-[30px] pt-[36px] w-full flex flex-col gap-5 lg:px-4">
          <div className="text-txtParagraph md:text-sm text-xs">
            By confirming your new plan, you agree to{" "}
            {companyDetails?.name ||
              companyDetails?.name ||
              companyDetails?.company_name}{" "}
            LLC’s{" "}
            <a
              target="_blank"
              href={"https://www.dcdial.com/legal/terms-of-service"}
              className="text-txtPrimary font-semibold hover:!text-blue hover:!underline"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              target="_blank"
              href={"https://www.dcdial.com/legal/privacy-policy"}
              className="text-txtPrimary font-semibold hover:!text-blue hover:!underline"
            >
              Privacy Policy
            </a>
            .
          </div>
          <Button
            text="Confirm"
            isLoading={isLoading}
            variant="variable"
            onClick={() => {
              handleSubmit();
            }}
          />
          <Button
            isDisabled={isLoading}
            text="Back"
            variant="white"
            onClick={() => setCurrentStep((state: number) => state - 1)}
          />
        </div>
      </div>
    );
  }
);

export default UpdateSubscribePreview;
